import request from '../utils/request'

/**
 * 按桥梁ID和设备类型查询历史数据（支持WATER_LEVEL、ANEMOMETER、VISIBILITY和FLOWMETER）
 * @param {number} bridgeId - 桥梁ID
 * @param {string} deviceType - 设备类型（WATER_LEVEL、ANEMOMETER、VISIBILITY、FLOWMETER）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {number} pageNum - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       deviceId: number,
 *       deviceName: string,
 *       deviceType: string,
 *       deviceSubType: string,
 *       dataTimestamp: string,
 *       realTimeValue: number|object,
 *       extraProperties: object,
 *       ip: string,
 *       port: number,
 *       unit: string
 *     }>,
 *     pageNum: number,
 *     pageSize: number,
 *     total: number
 *   }
 * }>} 设备历史数据列表
 */
export const getHistoryDataByBridge = (bridgeId, deviceType, startTime, endTime, pageNum, pageSize) => {
  const params = {
    bridgeId,
    deviceType
  }
  
  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime
  if (pageNum) params.pageNum = pageNum
  if (pageSize) params.pageSize = pageSize
  
  return request({
    url: '/device-history-data/by-bridge',
    method: 'get',
    params
  })
}

/**
 * 获取水位计历史数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} startTime - 开始时间（可选）
 * @param {string} endTime - 结束时间（可选）
 * @param {number} pageNum - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       deviceId: number,
 *       deviceName: string,
 *       deviceType: string,
 *       deviceSubType: string,
 *       dataTimestamp: string,
 *       realTimeValue: number|object,
 *       extraProperties: object,
 *       ip: string,
 *       port: number,
 *       unit: string
 *     }>,
 *     pageNum: number,
 *     pageSize: number,
 *     total: number
 *   }
 * }>} 水位计历史数据列表
 */
export const getWaterLevelHistoryData = (bridgeId, startTime, endTime, pageNum, pageSize) => {
  return getHistoryDataByBridge(bridgeId, 'WATER_LEVEL', startTime, endTime, pageNum, pageSize)
}

/**
 * 获取风速计历史数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} startTime - 开始时间（可选）
 * @param {string} endTime - 结束时间（可选）
 * @param {number} pageNum - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       deviceId: number,
 *       deviceName: string,
 *       deviceType: string,
 *       deviceSubType: string,
 *       dataTimestamp: string,
 *       realTimeValue: number|object,
 *       extraProperties: object,
 *       ip: string,
 *       port: number,
 *       unit: string
 *     }>,
 *     pageNum: number,
 *     pageSize: number,
 *     total: number
 *   }
 * }>} 风速计历史数据列表
 */
export const getAnemometerHistoryData = (bridgeId, startTime, endTime, pageNum, pageSize) => {
  return getHistoryDataByBridge(bridgeId, 'ANEMOMETER', startTime, endTime, pageNum, pageSize)
}

/**
 * 获取能见度计历史数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} startTime - 开始时间（可选）
 * @param {string} endTime - 结束时间（可选）
 * @param {number} pageNum - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       deviceId: number,
 *       deviceName: string,
 *       deviceType: string,
 *       deviceSubType: string,
 *       dataTimestamp: string,
 *       realTimeValue: number|object,
 *       extraProperties: object,
 *       ip: string,
 *       port: number,
 *       unit: string
 *     }>,
 *     pageNum: number,
 *     pageSize: number,
 *     total: number
 *   }
 * }>} 能见度计历史数据列表
 */
export const getVisibilityHistoryData = (bridgeId, startTime, endTime, pageNum, pageSize) => {
  return getHistoryDataByBridge(bridgeId, 'VISIBILITY', startTime, endTime, pageNum, pageSize)
}

/**
 * 获取能见度计统计聚合数据（用于图表展示）
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         avgVisibility: number,
 *         maxVisibility: number,
 *         minVisibility: number,
 *         dataCount: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 能见度统计聚合数据
 */
export const getVisibilityStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/visibility',
    method: 'get',
    params
  })
}

/**
 * 获取风速计统计聚合数据（用于图表展示）
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         avgWindSpeed: number,
 *         maxWindSpeed: number,
 *         minWindSpeed: number,
 *         dataCount: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 风速统计聚合数据
 */
export const getAnemometerStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/anemometer',
    method: 'get',
    params
  })
}

/**
 * 获取净高统计聚合数据（用于图表展示）
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         avgClearance: number,
 *         maxClearance: number,
 *         minClearance: number,
 *         dataCount: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 净高统计聚合数据
 */
export const getClearanceStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/clearance',
    method: 'get',
    params
  })
}

/**
 * 获取流速计历史数据
 * @param {number} bridgeId - 桥梁ID
 * @param {string} startTime - 开始时间（可选）
 * @param {string} endTime - 结束时间（可选）
 * @param {number} pageNum - 页码（可选）
 * @param {number} pageSize - 每页数量（可选）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       deviceId: number,
 *       dataTime: Array<number>,
 *       flowVelocity: number,
 *       clientIp: string,
 *       createTime: Array<number>,
 *       isDeleted: number
 *     }>,
 *     pageNum: number,
 *     pageSize: number,
 *     total: number
 *   }
 * }>} 流速计历史数据列表
 */
export const getFlowmeterHistoryData = (bridgeId, startTime, endTime, pageNum, pageSize) => {
  return getHistoryDataByBridge(bridgeId, 'FLOWMETER', startTime, endTime, pageNum, pageSize)
}

/**
 * 获取流速计统计聚合数据（用于图表展示）
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         avgFlowVelocity: number,
 *         maxFlowVelocity: number,
 *         minFlowVelocity: number,
 *         dataCount: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 流速计统计聚合数据
 */
export const getFlowmeterStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/flowmeter',
    method: 'get',
    params
  })
}

/**
 * 获取水位计统计聚合数据（用于图表展示）
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         avgWaterLevel: number,
 *         maxWaterLevel: number,
 *         minWaterLevel: number,
 *         dataCount: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 水位计统计聚合数据
 */
export const getWaterLevelStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/water-level',
    method: 'get',
    params
  })
}

/**
 * 获取张力计统计聚合数据（用于图表展示）- 多传感器版本
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     totalCount: number,
 *     startTime: string,
 *     endTime: string,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       extraData: {
 *         sensor1: {
 *           avg: number,
 *           max: number,
 *           min: number,
 *           count: number
 *         },
 *         sensor2: {
 *           avg: number,
 *           max: number,
 *           min: number,
 *           count: number
 *         },
 *         sensor3: {
 *           avg: number,
 *           max: number,
 *           min: number,
 *           count: number
 *         }
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number
 *     }
 *   }
 * }>} 张力计统计聚合数据（包含3个传感器的数据）
 */
export const getTensionmeterStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/tensionmeter',
    method: 'get',
    params
  })
}

/**
 * 获取倾角计加速度统计聚合数据（用于图表展示）- 多设备多维度
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     startTime: string,
 *     endTime: string,
 *     totalCount: number,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         vendorDeviceCode: string,
 *         dataCount: number,
 *         avgAccX: number,
 *         avgAccY: number,
 *         avgAccZ: number,
 *         minAccX: number,
 *         minAccY: number,
 *         minAccZ: number,
 *         maxAccX: number,
 *         maxAccY: number,
 *         maxAccZ: number,
 *         avgAngleX: number,
 *         avgAngleY: number,
 *         avgAngleZ: number,
 *         minAngleX: number,
 *         minAngleY: number,
 *         minAngleZ: number,
 *         maxAngleX: number,
 *         maxAngleY: number,
 *         maxAngleZ: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number,
 *       uniqueDeviceCount: number
 *     }
 *   }
 * }>} 倾角计加速度统计聚合数据（包含多个设备的加速度和角度数据）
 */
export const getInclinometerAccelerationStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/inclinometer/acceleration',
    method: 'get',
    params
  })
}

/**
 * 获取位移计绝对位移统计聚合数据（用于图表展示）- 多测点
 * @param {number} bridgeId - 桥梁ID（必填）
 * @param {string} granularity - 时间粒度（必填，'hour' 或 'day'）
 * @param {string} startTime - 开始时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @param {string} endTime - 结束时间（可选，格式：yyyy-MM-dd HH:mm:ss）
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     startTime: string,
 *     endTime: string,
 *     totalCount: number,
 *     timeSeriesData: Array<{
 *       time: string,
 *       timeLabel: string,
 *       value: number,
 *       extraData: {
 *         pointId: number,
 *         pointName: string,
 *         dataCount: number,
 *         avgDisplacementX: number,
 *         avgDisplacementY: number,
 *         avgDisplacementAbs: number,
 *         minDisplacementAbs: number,
 *         maxDisplacementAbs: number
 *       }
 *     }>,
 *     detailData: {
 *       granularity: string,
 *       dataPointCount: number,
 *       uniquePointCount: number
 *     }
 *   }
 * }>} 位移计绝对位移统计聚合数据（包含多个测点的X、Y、绝对位移数据）
 */
export const getDisplacementAbsoluteStatistics = (bridgeId, granularity, startTime, endTime) => {
  const params = {
    bridgeId,
    granularity
  }

  if (startTime) params.startTime = startTime
  if (endTime) params.endTime = endTime

  return request({
    url: '/device-history-data/statistics/displacement/absolute',
    method: 'get',
    params
  })
}

